import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { t } from '../utils/translations';
import { InfoModal, InfoPageKey } from './InfoModal';
import { Phone, Mail, Clock, MapPin, ArrowRight, Instagram, Facebook, Music2, Twitter, Youtube, ShieldCheck, Check } from 'lucide-react';
// Map each link (by group + index) to an info page key
const SERVICE_KEYS: InfoPageKey[] = ['residentialMoving', 'commercialMoving', 'furnitureAssembly', 'tvProtection', 'packing', 'sameWeek'];
const LEGAL_KEYS: InfoPageKey[] = ['terms', 'privacy', 'cancellation', 'insurance'];
const COMPANY_KEYS: InfoPageKey[] = ['about', 'fleet', 'coverageArea', 'careers', 'contact'];
const BOTTOM_KEYS: InfoPageKey[] = ['terms', 'privacy', 'cancellation'];
export function Footer() {
  const {
    lang
  } = useLanguage();
  const text = t[lang].footer;
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [openPage, setOpenPage] = useState<InfoPageKey | null>(null);
  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setTimeout(() => {
        setSubscribed(false);
        setEmail('');
      }, 3000);
    }
  };
  return <footer className="bg-mf-black text-white relative" data-id="element-155">
      {/* Top Red Help Bar */}
      <div className="bg-mf-red py-8 relative overflow-hidden" data-id="element-156">
        <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(#FFFFFF 1px, transparent 1px)',
        backgroundSize: '30px 30px'
      }} data-id="element-157"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-158">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-159">
            <div data-id="element-160">
              <h3 className="text-2xl md:text-4xl font-condensed font-black uppercase tracking-tight text-white" data-id="element-161">
                {text.helpBar}
              </h3>
              <p className="text-white/90 mt-1 text-sm md:text-base font-medium flex items-center" data-id="element-162">
                <ShieldCheck size={16} className="mr-2" data-id="element-163" />
                {text.helpSub}
              </p>
            </div>
            <a href="tel:+18134768935" className="inline-flex items-center justify-center bg-white text-mf-red px-8 py-4 rounded-2xl font-condensed font-black text-2xl tracking-wide hover:bg-mf-yellow hover:text-mf-black transition-colors shadow-xl whitespace-nowrap" data-id="element-164">
              <Phone size={24} className="mr-3" data-id="element-165" />
              (813) 476-8935
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-166">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-167">
          {/* Brand Column */}
          <div className="lg:col-span-4" data-id="element-168">
            <div className="flex items-center gap-4 mb-5" data-id="element-169">
              <img src="https://cdn.magicpatterns.com/uploads/87bxoPCzmrJpV9TJfPPsjR/IMG_4659.jpg" alt="Move Fighters" className="w-16 h-16 object-cover rounded-xl shadow-lg" data-id="element-170" />
              <div data-id="element-171">
                <div className="text-2xl font-condensed font-black uppercase tracking-tight" data-id="element-172">
                  MOVE FIGHTERS
                </div>
                <div className="text-sm text-gray-400" data-id="element-173">{text.tagline}</div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-2 mb-8" data-id="element-174">
              {text.badges.map((badge, i) => <span key={i} className="inline-flex items-center bg-white/5 border border-white/10 px-3 py-1.5 rounded-full text-xs text-gray-300 font-medium" data-id="element-175">
                  <Check size={12} className="mr-1.5 text-green-400" data-id="element-176" />
                  {badge}
                </span>)}
            </div>

            {/* Newsletter */}
            <div data-id="element-177">
              <h4 className="font-condensed font-black uppercase tracking-wider text-white mb-3 text-sm" data-id="element-178">
                {text.newsletterTitle}
              </h4>
              <form onSubmit={handleSubscribe} className="flex gap-2 mb-2" data-id="element-179">
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder={text.emailPlaceholder} className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-mf-red transition-colors" data-id="element-180" />
                <button type="submit" className="bg-mf-red hover:bg-red-700 text-white px-5 py-3 rounded-xl font-bold text-sm transition-colors flex items-center" data-id="element-181">
                  {subscribed ? <Check size={16} data-id="element-182" /> : <ArrowRight size={16} data-id="element-183" />}
                </button>
              </form>
              <p className="text-xs text-gray-500" data-id="element-184">{text.noSpam}</p>
            </div>
          </div>

          {/* Services Column */}
          <div className="lg:col-span-2" data-id="element-185">
            <h4 className="font-condensed font-black uppercase tracking-wider text-white mb-6 text-sm border-b border-white/10 pb-3" data-id="element-186">
              {text.servicesTitle}
            </h4>
            <ul className="space-y-3" data-id="element-187">
              {text.servicesLinks.map((link, i) => <li key={i} data-id="element-188">
                  <FooterLinkButton label={link} onClick={() => setOpenPage(SERVICE_KEYS[i] ?? null)} data-id="element-189" />
                </li>)}
            </ul>
          </div>

          {/* Legal & Company Column */}
          <div className="lg:col-span-3" data-id="element-190">
            <h4 className="font-condensed font-black uppercase tracking-wider text-white mb-6 text-sm border-b border-white/10 pb-3" data-id="element-191">
              {text.legalTitle}
            </h4>
            <ul className="space-y-3 mb-8" data-id="element-192">
              {text.legalLinks.map((link, i) => <li key={i} data-id="element-193">
                  <FooterLinkButton label={link} onClick={() => setOpenPage(LEGAL_KEYS[i] ?? null)} data-id="element-194" />
                </li>)}
            </ul>

            <h4 className="font-condensed font-black uppercase tracking-wider text-white mb-6 text-sm border-b border-white/10 pb-3" data-id="element-195">
              {text.companyTitle}
            </h4>
            <ul className="space-y-3" data-id="element-196">
              {text.companyLinks.map((link, i) => <li key={i} data-id="element-197">
                  <FooterLinkButton label={link} onClick={() => setOpenPage(COMPANY_KEYS[i] ?? null)} data-id="element-198" />
                </li>)}
            </ul>
          </div>

          {/* Contact Column */}
          <div className="lg:col-span-3" data-id="element-199">
            <h4 className="font-condensed font-black uppercase tracking-wider text-white mb-6 text-sm border-b border-white/10 pb-3" data-id="element-200">
              {text.contactTitle}
            </h4>

            <ul className="space-y-5 mb-6" data-id="element-201">
              <li data-id="element-202">
                <a href="tel:+18134768935" className="flex items-start group" data-id="element-203">
                  <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mr-3 group-hover:bg-mf-red group-hover:border-mf-red transition-colors flex-shrink-0" data-id="element-204">
                    <Phone size={16} className="text-mf-red group-hover:text-white" data-id="element-205" />
                  </div>
                  <div data-id="element-206">
                    <div className="font-bold text-white text-base" data-id="element-207">
                      (813) 476-8935
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-208">{text.callNow}</div>
                  </div>
                </a>
              </li>

              <li data-id="element-209">
                <a href="mailto:movefighters@gmail.com" className="flex items-start group" data-id="element-210">
                  <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mr-3 group-hover:bg-mf-red group-hover:border-mf-red transition-colors flex-shrink-0" data-id="element-211">
                    <Mail size={16} className="text-mf-red group-hover:text-white" data-id="element-212" />
                  </div>
                  <div data-id="element-213">
                    <div className="font-bold text-white text-sm break-all" data-id="element-214">
                      movefighters@gmail.com
                    </div>
                  </div>
                </a>
              </li>

              <li className="flex items-start" data-id="element-215">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-216">
                  <Clock size={16} className="text-mf-red" data-id="element-217" />
                </div>
                <div data-id="element-218">
                  <div className="font-bold text-white text-sm" data-id="element-219">
                    {text.hours}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-220">
                    {text.hoursSunday}
                  </div>
                </div>
              </li>

              <li className="flex items-start" data-id="element-221">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-222">
                  <MapPin size={16} className="text-mf-red" data-id="element-223" />
                </div>
                <div data-id="element-224">
                  <div className="font-bold text-white text-sm" data-id="element-225">
                    {text.address}
                  </div>
                </div>
              </li>
            </ul>

            {/* Social Media */}
            <div className="flex gap-2 mb-4" data-id="element-226">
              <SocialIcon icon={<Instagram size={16} data-id="element-228" />} href="#" data-id="element-227" />
              <SocialIcon icon={<Facebook size={16} data-id="element-230" />} href="#" data-id="element-229" />
              <SocialIcon icon={<Music2 size={16} data-id="element-232" />} href="#" data-id="element-231" />
              <SocialIcon icon={<Youtube size={16} data-id="element-234" />} href="#" data-id="element-233" />
              <SocialIcon icon={<Twitter size={16} data-id="element-236" />} href="#" data-id="element-235" />
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-237">
          <div className="text-xs text-gray-500 flex items-center" data-id="element-238">
            <ShieldCheck size={14} className="mr-2 text-green-400" data-id="element-239" />
            <span data-id="element-240">USDOT • MC • FL Mover Registration</span>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 bg-black/40" data-id="element-241">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-242">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-243">
            <p className="text-sm text-gray-500 text-center md:text-left" data-id="element-244">
              {text.rights}
            </p>
            <div className="flex items-center gap-6 text-xs text-gray-500" data-id="element-245">
              {text.bottomLinks.map((link, i) => <button key={i} type="button" onClick={() => setOpenPage(BOTTOM_KEYS[i] ?? null)} className="hover:text-white transition-colors" data-id="element-246">
                  {link}
                </button>)}
            </div>
          </div>
        </div>
      </div>

      <InfoModal pageKey={openPage} onClose={() => setOpenPage(null)} data-id="element-247" />
    </footer>;
}
// ----- Sub-components -----
function FooterLinkButton({
  label,
  onClick
}: {
  label: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className="text-sm text-gray-400 hover:text-mf-yellow transition-colors flex items-center group text-left w-full" data-id="element-248">
      <span className="w-1 h-1 rounded-full bg-mf-red mr-2 group-hover:w-2 transition-all" data-id="element-249"></span>
      {label}
    </button>;
}
function SocialIcon({
  icon,
  href
}: {
  icon: React.ReactNode;
  href: string;
}) {
  return <a href={href} className="w-9 h-9 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center text-gray-400 hover:bg-mf-red hover:border-mf-red hover:text-white transition-colors" data-id="element-250">
      {icon}
    </a>;
}
// Proper brand-colored payment logos
function PaymentLogo({
  type
}: {
  type: 'visa' | 'mastercard' | 'amex' | 'discover' | 'apple' | 'google' | 'cash';
}) {
  const pill = 'h-7 bg-white rounded flex items-center justify-center shadow-sm overflow-hidden';
  switch (type) {
    case 'visa':
      return <div className={`${pill} w-12`} data-id="element-251">
          <span className="text-[11px] font-black italic tracking-tight text-[#1A1F71]" data-id="element-252">
            VISA
          </span>
        </div>;
    case 'mastercard':
      return <div className={`${pill} w-12 relative`} data-id="element-253">
          <div className="w-4 h-4 rounded-full bg-[#EB001B] absolute left-1.5" data-id="element-254"></div>
          <div className="w-4 h-4 rounded-full bg-[#F79E1B] absolute right-1.5 mix-blend-multiply" data-id="element-255"></div>
        </div>;
    case 'amex':
      return <div className={`${pill} w-12 bg-[#2E77BB]`} data-id="element-256">
          <span className="text-[9px] font-black tracking-tight text-white" data-id="element-257">
            AMEX
          </span>
        </div>;
    case 'discover':
      return <div className={`${pill} w-14 relative`} data-id="element-258">
          <span className="text-[9px] font-black tracking-tight text-[#111]" data-id="element-259">
            DISC
          </span>
          <div className="w-3.5 h-3.5 rounded-full bg-[#FF6000] absolute right-1" data-id="element-260"></div>
        </div>;
    case 'apple':
      return <div className={`${pill} w-14 bg-black`} data-id="element-261">
          <svg viewBox="0 0 170 70" className="h-3.5 w-auto" aria-label="Apple Pay" data-id="element-262">
            {/* Apple logo */}
            <path fill="#fff" d="M22.9 19.1c1.4-1.7 2.3-4 2-6.4-2 .1-4.5 1.3-5.9 3-1.3 1.5-2.4 3.9-2.1 6.2 2.3.2 4.6-1.2 6-2.8zm2 3.2c-3.3-.2-6.1 1.9-7.7 1.9-1.6 0-4-1.8-6.6-1.7-3.4.1-6.6 2-8.3 5.1-3.5 6.1-.9 15.1 2.5 20.1 1.7 2.5 3.7 5.2 6.3 5.1 2.5-.1 3.5-1.6 6.5-1.6s3.9 1.6 6.6 1.6c2.7-.1 4.4-2.5 6.1-5 1.9-2.9 2.7-5.6 2.7-5.8-.1-.1-5.2-2-5.3-7.9-.1-4.9 4-7.3 4.2-7.4-2.3-3.4-5.9-3.8-7-3.4z" data-id="element-263" />
            {/* Pay text */}
            <text x="55" y="45" fill="#fff" fontFamily="-apple-system, system-ui, sans-serif" fontWeight="600" fontSize="30" data-id="element-264">
              Pay
            </text>
          </svg>
        </div>;
    case 'google':
      return <div className={`${pill} w-14 bg-white border border-gray-200`} data-id="element-265">
          <svg viewBox="0 0 200 80" className="h-3.5 w-auto" aria-label="Google Pay" data-id="element-266">
            {/* G in Google colors */}
            <text x="0" y="58" fontFamily="Arial, sans-serif" fontWeight="700" fontSize="48" data-id="element-267">
              <tspan fill="#4285F4" data-id="element-268">G</tspan>
              <tspan fill="#EA4335" data-id="element-269">o</tspan>
              <tspan fill="#FBBC05" data-id="element-270">o</tspan>
              <tspan fill="#4285F4" data-id="element-271">g</tspan>
              <tspan fill="#34A853" data-id="element-272">l</tspan>
              <tspan fill="#EA4335" data-id="element-273">e</tspan>
            </text>
            <text x="135" y="58" fill="#5F6368" fontFamily="Arial, sans-serif" fontWeight="500" fontSize="36" data-id="element-274">
              Pay
            </text>
          </svg>
        </div>;
    case 'cash':
      return <div className={`${pill} w-12`} data-id="element-275">
          <span className="text-[9px] font-black tracking-tight text-green-700" data-id="element-276">
            CASH
          </span>
        </div>;
  }
}